// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import Checkbox from "@components/Checkbox/Checkbox";
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName, getPropertiesByPrefix, parseDraftDataPropsToFieldState, delay} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";

import "react-datepicker/dist/react-datepicker.css";

export default class CobCustomerInformation extends React.PureComponent
{
    static propTypes = {
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        session: PropTypes.instanceOf(Session).isRequired,
        sectionNumber: PropTypes.number.isRequired,
        number: PropTypes.number.isRequired,
        prevState: PropTypes.object,
        onChange: PropTypes.func.isRequired
    };

    static companyExclusive = false;
    static privateExclusive = false;
    static fields = _getFields();

    state = _getInitialState.call(this);

    componentDidMount() {
        const {app, partner, session} = this.props;
        const fulfilledViewsById = {...app.fulfilledViewsById};

        if (!!session.lookupData["firstName"])
        {
            /**
             * This view is always fulfilled if we got the first name from a lookup
             * nothing to do for the user, just showing info.
             */
            for (let i = 0; i < partner.sections.length; i++) {
                if (partner.sections[i].name === "CustomerInformation") {
                    fulfilledViewsById["CustomerInformation"] = true;
                }
            }
        }

        this.props.actions.setAppState({fulfilledViewsById});
    }

    componentDidUpdate(prevProps, prevState)
    {
        const {props, state} = this;
        const {id, onChange} = props;

        if (prevState.fields !== state.fields)
        {
            onChange(id, {...state}, fulfillmentCheck(props, state));
        }

        if (prevProps.draftData !== props.draftData)
        {
            if (props.draftData)
            {
                // get the draftData props specific to this section only
                const draftDataProps = getPropertiesByPrefix(props.draftData, ["customer_", "contact_", "checkbox_", "radio_", "kyc_", "company_", "ubo_"]);
                // parse the draftData props into state.fields
                const newFieldsState = parseDraftDataPropsToFieldState(this.state.fields, draftDataProps);
                this.setState(
                    {
                        fields: {...newFieldsState}
                    },
                    async () =>
                    {
                        await delay(200);
                        onChange(id, {...this.state}, fulfillmentCheck(this.props, this.state));
                    }
                );
            }
        }
    }

    render()
    {
        const {props, state} = this;
        const {title, session, partner} = props;
        const classes = createClassName("Cob-CustomerInformation", {
            "Cob-View": true,
            "fulfilled": state.isFulfilled
        });

        let ssnLong = state.fields.customer_social_security_number.replaceAll("-", "");
        let ssnShort = ssnLong;
        if (ssnLong.length > 10)
        {
            ssnShort = ssnShort.substring(2, ssnShort.length);
        }

        return (
            <div className={classes}>
                <DefaultFields fields={this.constructor.fields} />

                <input
                    type="hidden"
                    name="checkbox_company"
                    value={session.isCompany ? "yes" : "no"}
                />

                <input type="hidden" name="house_comp_name" value={partner.companyName} />
                <input type="hidden" name="house_address_street" value="Engelbrektsgatan 5" />
                <input type="hidden" name="house_address_zipcode" value="114 32" />
                <input type="hidden" name="house_address_city" value="Stockholm" />
                <input type="hidden" name="house_comp_no" value="5568169626" />
                <input type="hidden" name="company_name" value={partner.companyName} />

                <div className="Cob-Fields">
                    <h2>{title}</h2>

                    {!session.lookupData && (
                        <FieldsCollection
                            fields={[
                                {
                                    render: (
                                        <Checkbox
                                            label="Är ett bolag"
                                            checked={session.isCompany}
                                            onChange={this._onIsCompanyChange}
                                        />
                                    )
                                }
                            ]}
                        />
                    )}

                    <FieldsCollection
                        fieldsPerRow={session.isCompany ? 2 : 3}
                        fields={[
                            {
                                render: (
                                    <EditableField
                                        name="customer_social_security_number"
                                        label={session.isCompany ? "Organisationsnummer" : "Personnummer"}
                                        placeholder={session.isCompany ? "XXXXXX-YYYY" : "ÅÅÅÅÅMMDD-XXXX"}
                                        value={state.fields.customer_social_security_number}
                                        readOnly={true}
                                        valid={true}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                        required={!partner._data.saveDraftButton}
                                    />
                                )
                            },
                            {
                                render: (
                                    <EditableField
                                        name="customer_firstname"
                                        label={session.isCompany ? "Bolag" : "Förnamn"}
                                        value={state.fields.customer_firstname}
                                        valid={!!session.lookupData["firstName"] || state.validFields.customer_firstname}
                                        readOnly={!!session.lookupData["firstName"]}
                                        required={!partner._data.saveDraftButton}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                    />
                                )
                            },
                            {
                                render: (
                                    <EditableField
                                        name="customer_lastname"
                                        label="Efternamn"
                                        value={state.fields.customer_lastname}
                                        valid={!!session.lookupData["lastName"] || state.validFields.customer_lastname}
                                        readOnly={!!session.lookupData["lastName"]}
                                        required={!partner._data.saveDraftButton}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                    />
                                ),
                                display: !session.isCompany
                            }
                        ]}
                    />

                    <FieldsCollection
                        fieldsPerRow={2}
                        fields={[
                            {
                                render: (
                                    <EditableField
                                        type="email"
                                        name="customer_email"
                                        label="E-postadress"
                                        value={state.fields.customer_email}
                                        valid={state.fields.customer_email.length > 0 && Validator.validateEmail(state.fields.customer_email)}
                                        onChange={this._onFieldChange}
                                        onBlur={this._onFieldBlur}
                                        required
                                    />
                                )
                            }
                        ]}
                    />

                    <input type="hidden" name="customer_name"
                           value={state.fields.customer_firstname + (session.isCompany ? "" : " " + state.fields.customer_lastname)} />
                    <input type="hidden" name="customer_social_security_number" value={session.isCompany ? ssnShort : ssnLong} />
                    <input type="hidden" name="customer_email" value={state.fields.customer_email} />
                </div>
            </div>
        );
    }

    // Internal methods
    _fieldValidator = (name, value) =>
    {
        if (name === "customer_social_security_number")
        {
            return this.props.session.isCompany ? Validator.validateOrgNum(value, true) : Validator.validateSsn(value, true);
        }
        else
        {
            return value.length > 0;
        }
    };

    _onFieldChange = (name, value) =>
    {
        CobHelpers.onViewFieldChange.call(this, name, value);
    };

    _onFieldBlur = (name) =>
    {
        CobHelpers.onViewFieldBlur.call(this, name);
    };

    _onCheckboxChange = (name, checked) =>
    {
        this.setState({
            fields: {
                ...this.state.fields,
                [name]: {...this.state.fields[name], checked}
            }
        }, () =>
        {
            const documentsRequired = [];

            if (this.state.fields.checkbox_idcheck_passport.checked)
            {
                documentsRequired.push("Kopia på pass");
            }

            if (this.state.fields.checkbox_idcheck_drivinglic.checked)
            {
                documentsRequired.push("Kopia på körkort");
            }

            if (this.state.fields.checkbox_idcheck_id.checked)
            {
                documentsRequired.push("Kopia på ID-kort");
            }

            this.props.actions.setAppState({
                documentsRequired: documentsRequired
            });
        });
    };

    _onIsCompanyChange = (checked) =>
    {
        const {session, onChange} = this.props;

        session.isCompany = checked;
        if (this.props.session.isCompany !== checked)
        {
            this.setState(_getInitialState.call(this), () =>
            {
                onChange({...this.state}, false, this.props.number);
            });
        }
    };

    _onOptionsListChange = ({type, name}, option, index) =>
    {
        const newOptions = [...this.state.fields[name].options].map((o, i) =>
        {
            if (i === index)
            {
                return option;
            }
            else
            {
                return {...o, checked: type === "radio" ? false : o.checked};
            }
        });

        this.setState({
            fields: {
                ...this.state.fields,
                [name]: {
                    options: newOptions
                }
            }
        }, () =>
        {
            fulfillmentCheck(this.props, this.state);
        });
    };
}


// PRIVATE FUNCTIONS
function _getFields()
{
    return [
        "customer_firstname",
        "customer_lastname",
        "customer_social_security_number",
        "customer_email"
    ];
}

function _getInitialState()
{
    if (this.props.prevState)
    {
        return {...this.props.prevState};
    }

    const {session} = this.props;
    const {isCompany} = session;
    const getLookupData = (name) => session.lookupData ? session.lookupData[name] || "" : "";

    return {
        busy: false,
        company: isCompany,
        validFields: !session.lookupData ? {} : {
            "customer_firstname": !!getLookupData(isCompany ? "companyName" : "firstName"),
            "customer_social_security_number": !!getLookupData(isCompany ? "orgNumber" : "ssn"),
            ...(
                !isCompany ? {
                    "customer_lastname": !!getLookupData("lastName")
                } : undefined
            )
        },
        fields: {
            "customer_firstname": getLookupData(isCompany ? "companyName" : "firstName"),
            "customer_lastname": !isCompany ? getLookupData("lastName") : "",
            "customer_social_security_number": getLookupData(isCompany ? "orgNumber" : "ssn"),
            "customer_email": ""
        },
    };
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Futur/Withdrawal/CustomerInformation.js